import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { Play, X, Video as VideoIcon, ChevronRight } from 'lucide-react';

const LatestVideosStrip = ({ user }) => {
  const [videos, setVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    api
      .get('/videos')
      .then((res) => {
        if (mounted) setVideos(res.data.slice(0, 6));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user]);

  if (!user || videos.length === 0) return null;

  return (
    <div className="mb-8" data-testid="latest-videos-strip">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
            <VideoIcon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Latest Videos
          </h2>
        </div>
        <Link
          to="/videos"
          data-testid="view-all-videos-link"
          className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
        >
          View all
          <ChevronRight className="w-4 h-4 ml-0.5" />
        </Link>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {videos.map((video) => (
          <button
            key={video.id}
            data-testid={`strip-video-${video.id}`}
            onClick={() => setActiveVideo(video)}
            className="group flex-shrink-0 w-64 snap-start text-left bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
          >
            <div className="relative w-full aspect-video bg-gray-900 overflow-hidden">
              <img
                src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                alt={video.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                <span className="w-11 h-11 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                </span>
              </span>
            </div>
            <div className="p-3">
              <span className="inline-block text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full mb-1.5">
                {video.category}
              </span>
              <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">{video.title}</h3>
            </div>
          </button>
        ))}
      </div>

      {activeVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
          data-testid="strip-video-modal"
        >
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-lg pr-4 line-clamp-1">{activeVideo.title}</h3>
              <button
                onClick={() => setActiveVideo(null)}
                data-testid="strip-close-player-btn"
                className="text-white/80 hover:text-white flex-shrink-0"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
              <iframe
                title={activeVideo.title}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${activeVideo.youtube_id}?autoplay=1&rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            {activeVideo.description && (
              <p className="text-gray-300 text-sm mt-3">{activeVideo.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LatestVideosStrip;
